import type { WorkflowDefinition } from "@/stores/workflowsStore";

/**
 * Resolves all {{$props.KEY}} references in a workflow definition by
 * replacing them with actual values from propValues. This "inlines"
 * template prop values so the resulting definition is self-contained.
 *
 * Whole-string references ("{{$props.KEY}}") preserve the original type
 * (number, boolean, etc.). Embedded references within a larger string
 * are interpolated as their string representation.
 */
export function resolvePropsInDefinition(
  definition: WorkflowDefinition,
  propValues: Record<string, unknown>,
): WorkflowDefinition {
  if (!definition?.nodes || !propValues || Object.keys(propValues).length === 0) {
    return definition;
  }

  const resolvedNodes: WorkflowDefinition["nodes"] = {};

  for (const [nodeId, node] of Object.entries(definition.nodes)) {
    resolvedNodes[nodeId] = {
      ...node,
      payload: node.payload ? resolvePropsInPayload(node.payload, propValues) : node.payload,
    };
  }

  return {
    ...definition,
    nodes: resolvedNodes,
  };
}

/**
 * Resolve `{{$props.KEY}}` and `{{$props.KEY.nested.path}}` references inside a
 * single payload object. Recurses through arrays and nested objects. Whole-string
 * references preserve the original value type (arrays/objects/numbers); embedded
 * references within a larger string interpolate via `JSON.stringify` for
 * object/array values, `String()` otherwise. Unresolved references pass through
 * untouched so downstream consumers can still see the original template.
 *
 * Shared by `resolvePropsInDefinition` (inliner), widget preview, and the
 * trigger test modal so every $props resolver speaks the same dialect.
 */
export function resolvePropsInPayload(
  payload: Record<string, unknown>,
  propValues: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!propValues || Object.keys(propValues).length === 0) return payload;
  return resolvePayloadWalk(payload, propValues);
}

function resolveValue(v: unknown, propValues: Record<string, unknown>): unknown {
  if (typeof v === "string") {
    return resolveStringValue(v, propValues);
  }
  if (Array.isArray(v)) {
    return v.map(elem => resolveValue(elem, propValues));
  }
  if (v !== null && typeof v === "object") {
    return resolvePayloadWalk(v as Record<string, unknown>, propValues);
  }
  return v;
}

/** Internal no-guard walker — callers already checked propValues is non-empty. */
function resolvePayloadWalk(
  payload: Record<string, unknown>,
  propValues: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    out[k] = resolveValue(v, propValues);
  }
  return out;
}

/**
 * Resolve a prop reference like `myProp` or `myProp.subKey.nested` against
 * propValues. Returns `{ hit: true, value }` when the head key exists and
 * every path segment traverses cleanly, or `{ hit: false }` otherwise.
 * Missing / wrong-typed segments produce `hit: false` so the caller can
 * preserve the original template string (backward compat).
 *
 * Scope: this resolver is for the `$props` variable space only. Other
 * nested-path helpers in the codebase (e.g. `DataTableWidget:getNestedValue`
 * for row-data sorting, `resolveVariablesFromRun:getNestedValue` for
 * `$result.*` runtime resolution, `resolvePayloadTemplates:getByPath` for
 * payload templates) handle DIFFERENT variable spaces with different
 * semantics — do not cargo-cult them into this function or vice versa.
 */
function lookupPropPath(
  reference: string,
  propValues: Record<string, unknown>,
): { hit: true; value: unknown } | { hit: false } {
  const segments = reference.split(".");
  const head = segments[0];
  if (!head || !(head in propValues)) return { hit: false };

  let current: unknown = propValues[head];
  for (let i = 1; i < segments.length; i++) {
    if (current == null || typeof current !== "object") return { hit: false };
    const key = segments[i];
    // Guard against trailing/empty segments (e.g. "foo." from a malformed template).
    // Without this, `Number("")` coerces to 0 and would match `arr[0]` accidentally.
    if (key === "") return { hit: false };
    // Support array numeric index (e.g. `recipientTable.address.0`).
    if (Array.isArray(current)) {
      const idx = Number(key);
      if (!Number.isInteger(idx) || idx < 0 || idx >= current.length) return { hit: false };
      current = current[idx];
      continue;
    }
    const obj = current as Record<string, unknown>;
    if (!(key in obj)) return { hit: false };
    current = obj[key];
  }
  return { hit: true, value: current };
}

function resolveStringValue(s: string, propValues: Record<string, unknown>): unknown {
  if (!s.includes("{{$props.")) return s;

  const trimmed = s.trim();

  // Whole-string reference — return typed value (arrays/objects preserved).
  if (trimmed.startsWith("{{") && trimmed.endsWith("}}")) {
    const inner = trimmed.slice(2, -2).trim();
    if (inner.startsWith("$props.")) {
      const reference = inner.slice("$props.".length).trim();
      if (reference && !reference.includes("{{")) {
        const result = lookupPropPath(reference, propValues);
        if (result.hit) return result.value ?? null;
        return s; // Key not resolvable — keep template string (backward compat)
      }
    }
  }

  // Embedded references — string interpolation. Arrays/objects stringify
  // via JSON.stringify so they survive (e.g. a log line referencing a list).
  return s.replace(/\{\{\$props\.([^}]+)\}\}/g, (match, key) => {
    const reference = String(key).trim();
    const result = lookupPropPath(reference, propValues);
    if (!result.hit) return match; // Key not resolvable — keep template string
    const val = result.value;
    if (val == null) return "";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  });
}
